import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Upload, ShoppingBag, Images, Home, Package, LayoutGrid, ArrowRight } from "lucide-react";
import { api, imgUrl, formatINR } from "../../lib/api";
import { cardCls } from "./shared";

const QUICK_ACTIONS = [
  { key: "products", label: "Add New Product", icon: Plus, testid: "quick-add-product" },
  { key: "bulk", label: "Bulk Upload Products", icon: Upload, testid: "quick-bulk-upload" },
  { key: "products", label: "Manage Products", icon: ShoppingBag, testid: "quick-manage-products" },
  { key: "portfolio", label: "Manage Portfolio", icon: Images, testid: "quick-manage-portfolio" },
  { key: "homepage", label: "Edit Homepage", icon: Home, testid: "quick-edit-homepage" },
];

export default function DashboardTab({ onNavigate }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading dashboard…</p>;

  const cards = [
    { label: "Total Products", value: stats.total_products, icon: ShoppingBag, testid: "stat-total-products" },
    { label: "Total Categories", value: stats.total_categories, icon: LayoutGrid, testid: "stat-total-categories" },
    { label: "Published", value: stats.published_products, icon: Package, testid: "stat-published" },
    { label: "Drafts", value: stats.draft_products, icon: Package, testid: "stat-drafts" },
    { label: "Total Orders", value: stats.total_orders, icon: Package, testid: "stat-orders" },
    { label: "Orders To Action", value: stats.open_orders, icon: Package, testid: "stat-open-orders" },
  ];

  return (
    <div data-testid="admin-dashboard-tab" className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map(({ label, value, icon: Icon, testid }) => (
          <div key={testid} className="glass rounded-2xl p-4 text-center" data-testid={testid}>
            <Icon className="mx-auto text-primary" size={18} />
            <p className="mt-2 font-display text-2xl">{value}</p>
            <p className="mt-1 text-[10px] tracking-[0.15em] text-muted-foreground uppercase">{label}</p>
          </div>
        ))}
      </div>

      <div className={cardCls} data-testid="quick-actions">
        <p className="font-display text-lg">Quick actions</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {QUICK_ACTIONS.map(({ key, label, icon: Icon, testid }) => (
            <button key={testid} data-testid={testid} onClick={() => onNavigate(key)}
              className="flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs tracking-[0.15em] text-white uppercase transition-colors hover:bg-primary">
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={cardCls} data-testid="recent-products">
          <div className="flex items-center justify-between">
            <p className="font-display text-lg">Recent products</p>
            <button onClick={() => onNavigate("products")} className="flex items-center gap-1 text-xs tracking-widest uppercase hover:text-primary" data-testid="recent-products-all">
              Manage <ArrowRight size={12} />
            </button>
          </div>
          <ul className="mt-4 space-y-3">
            {stats.recent_products.map((p) => (
              <li key={p.id} className="flex items-center gap-3" data-testid={`recent-product-${p.id}`}>
                {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-10 w-8 rounded-lg object-cover" /> : <span className="flex h-10 w-8 items-center justify-center rounded-lg bg-secondary font-display">A</span>}
                <span className="line-clamp-1 flex-1 text-sm font-light">{p.name}</span>
                <span className="text-xs text-muted-foreground">{formatINR(p.price)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[9px] tracking-widest uppercase ${p.is_active ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.is_active ? "live" : "draft"}</span>
              </li>
            ))}
            {stats.recent_products.length === 0 && <li className="text-sm font-light text-muted-foreground">No products yet.</li>}
          </ul>
        </div>

        <div className={cardCls} data-testid="recent-updates">
          <div className="flex items-center justify-between">
            <p className="font-display text-lg">Recent orders</p>
            <button onClick={() => onNavigate("orders")} className="flex items-center gap-1 text-xs tracking-widest uppercase hover:text-primary" data-testid="recent-orders-all">
              View all <ArrowRight size={12} />
            </button>
          </div>
          <ul className="mt-4 space-y-3">
            {stats.recent_orders.map((o) => (
              <li key={o.order_reference} className="flex items-center justify-between gap-3 text-sm font-light" data-testid={`recent-order-${o.order_reference}`}>
                <span>{o.order_reference} <span className="text-xs text-muted-foreground">· {o.customer?.name || ""}</span></span>
                <span className="text-xs text-muted-foreground">{formatINR(o.total)} · <span className="uppercase">{o.order_status}</span></span>
              </li>
            ))}
            {stats.recent_orders.length === 0 && <li className="text-sm font-light text-muted-foreground">No orders yet.</li>}
          </ul>
          {stats.settings_updated_at && (
            <p className="mt-4 border-t border-input/60 pt-3 text-[11px] font-light text-muted-foreground" data-testid="last-site-update">
              Website content last updated: {new Date(stats.settings_updated_at).toLocaleString("en-IN")}
            </p>
          )}
        </div>
      </div>

      <p className="text-center text-xs font-light text-muted-foreground">
        Need the storefront? <Link to="/" className="underline hover:text-primary" data-testid="dashboard-view-store">Open your live boutique</Link>
      </p>
    </div>
  );
}
